import { useCallback } from "react";
import { ReactCropperElement } from "react-cropper";
import { getCurrentCropInOriginalCoordinates } from "../utils";
import { VLCropperProps } from "../types";
import { MIN_CROP_SIZE } from "../constants";

interface UseCropperOperationsProps {
  cropperRef: React.RefObject<ReactCropperElement>;
  onCropChange?: VLCropperProps["onCropChange"];
  resetState: () => void;
  setClickPosition: (position: { x: number; y: number }) => void;
  setHasInitialCrop: (value: boolean) => void;
}

/**
 * Custom hook for cropper operations (reset, getCropData, etc.)
 */
export const useCropperOperations = ({
  cropperRef,
  onCropChange,
  resetState,
  setClickPosition,
  setHasInitialCrop,
}: UseCropperOperationsProps) => {
  const resetCrop = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const container = cropper.getContainerData();
    cropper.clear();
    resetState();
    cropper.setDragMode("crop");

    setClickPosition({
      x: container.width / 2,
      y: container.height / 2,
    });

    onCropChange?.(false, null);
  }, [cropperRef, resetState, setClickPosition, onCropChange]);

  const getCropData = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return { croppedCanvas: null, cropData: null };

    const croppedCanvas = cropper.getCroppedCanvas();
    const cropData = cropper.getData();

    return {
      croppedCanvas,
      cropData: {
        x: cropData.x,
        y: cropData.y,
        width: cropData.width,
        height: cropData.height,
      },
    };
  }, [cropperRef]);

  const setupAutoCrop = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCanvasData();
    if (!canvas) return;

    const width = MIN_CROP_SIZE;
    const height = MIN_CROP_SIZE;
    const left = canvas.left + (canvas.width - width) / 2;
    const top = canvas.top + (canvas.height - height) / 2;

    cropper.setCropBoxData({ width, height, left, top });
    cropper.crop();

    setHasInitialCrop(true);
    setClickPosition({ x: left + width / 2, y: top + height / 2 });

    const currentCrop = getCurrentCropInOriginalCoordinates(cropperRef);
    onCropChange?.(true, currentCrop);
  }, [cropperRef, setHasInitialCrop, setClickPosition, onCropChange]);

  return {
    resetCrop,
    getCropData,
    setupAutoCrop,
  };
};
